// This file contains the dataLayer objects for eCommerce events as provided by Datalicious

import { LegacyAnalyticsEventData } from '@scentregroup/shared/analytics'
import { ITrackingEvent } from './events'
// import { IServiceFeeCartItem } from '../../client/types/cart'
import { IProductFormState, ScheduleOptions } from '../../client/types/product'
import { IProductValues } from '../../client/types/product-form'
import {
  DGC_AFFILIATION,
  IEcommerceDGCProduct,
} from '@scentregroup/shared/analytics/types/events/legacy'
import {
  IGiftCardCartItem,
  IServiceFeeCartItem,
} from '../../../../../src/types/cart'
import moment from 'moment'

const PRODUCT_NAME = 'Digital Gift Card'
const SERVICE_FEE_PRODUCT_NAME = 'Digital Gift Card Service Fee'
const PRODUCT_BRAND = 'Westfield Gift Cards'
const PRODUCT_CATEGORY = 'Gift Card/Digital Gift Card'
const CURRENCY_CODE = 'AUD'

const determineScheduleOption = (date: string): ScheduleOptions => {
  return moment(date).isSame(moment(), 'day')
    ? ScheduleOptions.SEND_IMMEDIATELY
    : ScheduleOptions.SCHEDULE_LATER
}

export const createAddToCartTrackingEvent = (
  values: IProductValues,
  skuId: string,
  state: IProductFormState
): LegacyAnalyticsEventData => {
  return {
    event: 'add_to_cart',
    ecommerce: {
      currencyCode: CURRENCY_CODE,
      affiliation: DGC_AFFILIATION,
      add: {
        products: [
          {
            name: PRODUCT_NAME,
            id: skuId,
            category: PRODUCT_CATEGORY,
            brand: PRODUCT_BRAND,
            price: values.amount.value.toFixed(2),
            quantity: 1,
            dimension91: values.amount.value.toFixed(2),
            dimension92: state.recipientExpanded ? 'Recipient' : 'Purchaser',
            dimension93: values.deliveryMethod as string,
            dimension94: values.deliveryDate,
            dimension100: 'AU',
            dimension116: determineScheduleOption(values.deliveryDate),
          },
        ],
      },
    },
  }
}

export const createRemoveFromCartTrackingEvent = (
  item: IGiftCardCartItem
): LegacyAnalyticsEventData => {
  return {
    event: 'remove_from_cart',
    ecommerce: {
      currencyCode: CURRENCY_CODE,
      affiliation: DGC_AFFILIATION,
      remove: {
        products: [
          {
            name: PRODUCT_NAME,
            id: item.sku,
            category: PRODUCT_CATEGORY,
            brand: PRODUCT_BRAND,
            price: item.value.toFixed(2),
            quantity: 1,
            dimension91: item.value.toFixed(2),
            dimension92: item.recipientDetails.senderName
              ? 'Recipient'
              : 'Purchaser',
            dimension93: item.recipientDetails.deliveryMethod as string,
            dimension94: item.recipientDetails.deliveryDate,
            dimension100: 'AU',
            dimension116: determineScheduleOption(
              item.recipientDetails.deliveryDate
            ),
          },
        ],
      },
    },
  }
}

export const createCheckoutStepTrackingEvent = (
  event: 'view_cart' | 'begin_checkout' | 'add_payment_info',
  giftCardItems: IGiftCardCartItem[],
  serviceFeeItem?: IServiceFeeCartItem,
  billingDetails?: ITrackedBillingDetails
): LegacyAnalyticsEventData => {
  const products = itemsToProducts(giftCardItems, billingDetails)
  if (serviceFeeItem) {
    products.push(serviceFeeToProduct(serviceFeeItem, billingDetails))
  }
  return {
    event,
    ecommerce: {
      currencyCode: CURRENCY_CODE,
      affiliation: DGC_AFFILIATION,
      checkout: {
        actionField: { step: event === 'add_payment_info' ? 2 : 1 },
        products,
      },
    },
  }
}

export const createPurchaseTrackingEvent = (
  cartId: string | undefined, //Segment needs the cart ID, so including here.
  orderId: string,
  orderAmount: number,
  giftCardItems: IGiftCardCartItem[],
  serviceFeeItem?: IServiceFeeCartItem,
  billingDetails?: ITrackedBillingDetails
): LegacyAnalyticsEventData => {
  const products = itemsToProducts(giftCardItems, billingDetails)
  if (serviceFeeItem) {
    products.push(serviceFeeToProduct(serviceFeeItem, billingDetails))
  }
  return {
    event: 'purchase',
    ecommerce: {
      currencyCode: CURRENCY_CODE,
      affiliation: DGC_AFFILIATION,
      purchase: {
        actionField: {
          id: orderId,
          cartId,
          affiliation: DGC_AFFILIATION,
          revenue: orderAmount.toFixed(2),
          tax: calculateAustralianGSTInDollars(orderAmount).toFixed(2),
          shipping: (0).toFixed(2),
        },
        products,
      },
    },
  }
}

export const createViewItemTrackingEvent = (
  skuId: string,
  amount: number
): LegacyAnalyticsEventData => {
  return {
    event: 'view_item',
    ecommerce: {
      currencyCode: CURRENCY_CODE,
      affiliation: DGC_AFFILIATION,
      detail: {
        products: [
          {
            name: PRODUCT_NAME,
            id: skuId,
            category: PRODUCT_CATEGORY,
            brand: PRODUCT_BRAND,
            price: amount.toFixed(2),
            dimension100: 'AU',
            dimension91: amount.toFixed(2),
          },
        ],
      },
    },
  }
}

export const createProductDetailevent = (
  id: string,
  minValue: number,
  maxValue: number
): ITrackingEvent => {
  return {
    event: 'productDetail',
    ecommerce: {
      detail: {
        actionField: { list: 'digital gift cards' },
        products: {
          name: 'digital gift card',
          id: id,
          category: 'digital',
          brand: 'westfield',
          position: 1,
          price: minValue + ' - ' + maxValue,
          list: 'digital gift cards',
          dimension91: '',
        },
      },
    },
  }
}

const itemsToProducts = (
  giftCardItems: IGiftCardCartItem[],
  billingDetails?: ITrackedBillingDetails
): (IEcommerceDGCProduct & { quantity: number })[] => {
  const billingDimensions = billingDetails
    ? billingDetailsToDimensions(billingDetails)
    : undefined
  return giftCardItems.map(item => ({
    name: PRODUCT_NAME,
    id: item.sku,
    category: PRODUCT_CATEGORY,
    brand: PRODUCT_BRAND,
    price: item.value.toFixed(2),
    quantity: 1,
    dimension91: item.value.toFixed(2),
    dimension92: item.recipientDetails.senderName ? 'Recipient' : 'Purchaser',
    dimension93: item.recipientDetails.deliveryMethod as string,
    dimension94: item.recipientDetails.deliveryDate,
    dimension100: 'AU',
    dimension116: determineScheduleOption(item.recipientDetails.deliveryDate),
    ...billingDimensions,
  }))
}

const serviceFeeToProduct = (
  serviceFeeItem: IServiceFeeCartItem,
  billingDetails?: ITrackedBillingDetails
): IEcommerceDGCProduct & { quantity: number } => {
  let product = {
    name: SERVICE_FEE_PRODUCT_NAME,
    id: serviceFeeItem.sku,
    category: PRODUCT_CATEGORY,
    brand: PRODUCT_BRAND,
    price: serviceFeeItem.salesPrice.toFixed(2),
    quantity: serviceFeeItem.quantity,
  }
  if (billingDetails) {
    product = {
      ...product,
      ...billingDetailsToDimensions(billingDetails),
    }
  }
  return product
}

const billingDetailsToDimensions = (
  billingDetails: ITrackedBillingDetails
): ITrackedBillingDimensions => {
  return {
    dimension95: billingDetails.suburb,
    dimension96: billingDetails.state,
    dimension97: billingDetails.postcode,
    dimension98: billingDetails.cardBrand,
    dimension100: billingDetails.countryCode,
    dimension117: billingDetails.paymentType,
  }
}

export type TrackingPaymentType = 'Credit Card' | 'Apple Pay' | 'Google Pay'

type ITrackedBillingDetails = {
  suburb: string
  state: string
  postcode: string
  cardBrand?: string
  countryCode: string
  paymentType?: TrackingPaymentType
}

type ITrackedBillingDimensions = {
  dimension95: string
  dimension96: string
  dimension97: string
  dimension98?: string
  dimension100: string
  dimension117?: TrackingPaymentType
}

export function calculateAustralianGSTInDollars(
  amountInDollars: number
): number {
  const amountInCents = amountInDollars * 100
  return +((amountInCents * (1 / 11)) / 100).toFixed(2)
}
